/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */

import {
  EmployeeNameInput,
  formatEmployeeNameParts,
} from '@/core/util/helper/employeeDisplayName';

export const BUZZ_MENTION_PATTERN = /@\[(\d+)\|([^\]]+)\]/g;

export interface BuzzMentionSegment {
  type: 'text' | 'mention' | 'link';
  value: string;
  empNumber?: number;
  href?: string;
}

const BUZZ_URL_PATTERN = /(?:https?:\/\/|www\.)[^\s]+/gi;

const trimUrlTrailingPunctuation = (url: string): string => {
  return url.replace(/[.,!?;:)\]}>]+$/, '');
};

const normalizeBuzzLinkHref = (url: string): string => {
  return /^www\./i.test(url) ? `https://${url}` : url;
};

export const splitTextByUrls = (text: string): BuzzMentionSegment[] => {
  if (!text) {
    return [];
  }

  const segments: BuzzMentionSegment[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(BUZZ_URL_PATTERN)) {
    const rawUrl = match[0];
    const url = trimUrlTrailingPunctuation(rawUrl);
    if (!url) {
      continue;
    }

    const start = match.index ?? 0;
    if (start > lastIndex) {
      segments.push({
        type: 'text',
        value: text.slice(lastIndex, start),
      });
    }

    segments.push({
      type: 'link',
      value: url,
      href: normalizeBuzzLinkHref(url),
    });

    lastIndex = start + url.length;
  }

  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      value: text.slice(lastIndex),
    });
  }

  return segments.length > 0 ? segments : [{type: 'text', value: text}];
};

const expandTextSegmentsWithLinks = (
  segments: BuzzMentionSegment[],
): BuzzMentionSegment[] => {
  return segments.flatMap((segment) =>
    segment.type === 'text' ? splitTextByUrls(segment.value) : [segment],
  );
};

export const formatBuzzMentionToken = (
  empNumber: number,
  userName: string,
): string => {
  return `@[${empNumber}|${userName.trim()}]`;
};

export const resolveBuzzMentionUserName = (employee: {
  userName?: string | null;
  employeeId?: string | null;
  firstName?: string;
  middleName?: string | null;
  lastName?: string;
  nickname?: string | null;
  nickName?: string | null;
}): string => {
  const userName = employee.userName?.trim();
  if (userName) {
    return userName;
  }

  const employeeId = employee.employeeId?.trim();
  if (employeeId) {
    return employeeId;
  }

  return formatEmployeeDisplayName(
    {
      firstName: employee.firstName ?? '',
      middleName: employee.middleName,
      lastName: employee.lastName ?? '',
      nickname: employee.nickname,
      nickName: employee.nickName,
    },
    {preferNickname: true},
  );
};

export const parseBuzzMentionText = (text: string): BuzzMentionSegment[] => {
  if (!text) {
    return [];
  }

  const segments: BuzzMentionSegment[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(BUZZ_MENTION_PATTERN)) {
    const [token, empNumber, displayName] = match;
    const start = match.index ?? 0;

    if (start > lastIndex) {
      let value = text.slice(lastIndex, start);
      if (!segments.length) {
        value = value.replace(/^\s+/, '');
      }
      if (value) {
        segments.push({
          type: 'text',
          value,
        });
      }
    }

    segments.push({
      type: 'mention',
      value: displayName.trim(),
      empNumber: Number(empNumber),
    });

    lastIndex = start + token.length;
  }

  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      value: text.slice(lastIndex),
    });
  }

  return expandTextSegmentsWithLinks(segments);
};

export const formatEmployeeDisplayName = (
  employee: EmployeeNameInput,
  options?: {preferNickname?: boolean},
): string => {
  return formatEmployeeNameParts(employee, {
    preferNickname: options?.preferNickname ?? true,
    includeMiddle: true,
  });
};

export const extractFirstBuzzUrl = (text: string): string | null => {
  if (!text) {
    return null;
  }

  const segments = splitTextByUrls(text);
  const linkSegment = segments.find((segment) => segment.type === 'link');
  return linkSegment?.href ?? null;
};
